<template>
  <div class="history">
    <v-base-card>
      <template v-slot:heading>
        プリセット一覧
      </template>

      <template v-slot:content>
        <v-row class="mb-0 pb-0">
          <v-col cols="2">
            <v-select
              v-model="selectedSubjectId"
              :items="subjects"
              item-text="name"
              item-value="id"
              label="教科"
              clearable
            ></v-select>
          </v-col>
          <v-col cols="2">
            <v-select
              v-model="selectedGradeId"
              :items="grades"
              item-text="name"
              item-value="id"
              label="学年"
              clearable
            ></v-select>
          </v-col>
          <v-col cols="2">
            <v-select
              v-model="selectedStatus"
              :items="statusOptions"
              item-text="text"
              item-value="value"
              label="ステータス"
              clearable
            ></v-select>
          </v-col>
          <v-col cols="2">
            <v-text-field
              v-model="keyword"
              label="キーワード検索"
              clearable
              @keyup.enter="search"
            />
          </v-col>
          <v-col cols="1" class="d-flex align-center">
            <v-btn small color="secondary" @click="search">検索</v-btn>
          </v-col>
          <v-col cols="2" class="d-flex align-center" style="gap: 8px">
            <v-btn
              color="primary"
              small
              :loading="exporting"
              @click="exportXlsx"
            >
              XLSX出力
            </v-btn>
            <v-btn
              small
              color="#4a81aa"
              dark
              @click="showImportArea = !showImportArea"
            >
              XLSX取込
            </v-btn>
          </v-col>
          <v-col cols="1" class="d-flex align-center">
            <v-btn
              color="primary"
              small
              @click="createNew"
            >
              新規作成
            </v-btn>
          </v-col>
        </v-row>
        <v-expand-transition>
          <div v-if="showImportArea" class="import-area mx-2 mb-3">
            <!-- 取込結果 -->
            <div v-if="importResults.length" class="mb-3">
              <div
                v-for="(result, idx) in importResults"
                :key="idx"
                class="import-result-item"
                :class="{ 'mb-2': idx < importResults.length - 1 }"
              >
                <div class="d-flex align-center">
                  <v-icon x-small class="mr-1" :color="result.success ? 'green' : 'red'">
                    {{ result.success ? 'mdi-check-circle' : 'mdi-close-circle' }}
                  </v-icon>
                  <span class="text-caption font-weight-medium">{{ result.filename }}</span>
                  <span class="text-caption ml-2">{{ result.message }}</span>
                  <v-spacer></v-spacer>
                  <v-btn icon x-small @click="removeResult(idx)"><v-icon x-small>mdi-close</v-icon></v-btn>
                </div>
              </div>
            </div>
            <!-- ドロップゾーン -->
            <div
              class="dropzone-area"
              :class="{ 'dropzone-active': dragging }"
              @dragover.prevent="dragging = true"
              @dragenter.prevent="dragging = true"
              @dragleave.prevent="dragging = false"
              @drop.prevent="onDrop"
              @click="$refs.fileInput.click()"
            >
              <input
                ref="fileInput"
                type="file"
                accept=".xlsx,.xls"
                multiple
                style="display: none"
                @change="onFileSelect"
              >
              <div v-if="!importFiles.length" class="dropzone-placeholder">
                <v-icon size="28" color="grey">mdi-file-upload-outline</v-icon>
                <div class="text-body-2 grey--text mt-1">XLSXをドロップまたはクリック</div>
              </div>
              <div v-else>
                <div
                  v-for="(file, i) in importFiles"
                  :key="i"
                  class="dropzone-selected d-flex align-center"
                  :class="{ 'mt-1': i > 0 }"
                >
                  <v-icon small class="mr-2" color="#4a81aa">mdi-file-excel</v-icon>
                  <span class="text-body-2">{{ file.name }}</span>
                  <v-spacer></v-spacer>
                  <v-btn icon x-small @click.stop="removeFile(i)"><v-icon x-small>mdi-close</v-icon></v-btn>
                </div>
              </div>
            </div>
            <v-btn
              small
              color="#4a81aa"
              dark
              class="mt-2"
              :loading="uploading"
              :disabled="!importFiles.length"
              @click="startImport"
            >
              取込開始（{{ importFiles.length }}ファイル）
            </v-btn>
          </div>
        </v-expand-transition>
        <v-data-table
          :headers="headers"
          :items="presets"
          :loading="loading"
          loading-text="Loading... Please wait"
          hide-default-footer
          :items-per-page="-1"
          :height="'calc(100vh - 300px)'"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
        >
          <!-- 進学教材プリセットの年度(R8/R9の出し分けに使う)。未設定はR8として扱う -->
          <template #[`item.fiscal_year_western`]="{ item }">
            <template v-if="item.is_shingaku">{{ item.fiscal_year_western ? `R${item.fiscal_year_western - 2018}` : '未設定' }}</template>
          </template>
          <template #[`item.status`]="{ item }">
            <v-chip :color="item.status === 'published' ? 'green' : 'grey'" dark small>
              {{ item.status === 'published' ? '公開中' : '下書き' }}
            </v-chip>
          </template>
          <template #[`item.has_pdf`]="{ item }">
            <v-icon v-if="item.has_pdf" color="green" small>mdi-check-circle</v-icon>
            <v-icon v-else color="grey" small>mdi-close-circle</v-icon>
          </template>
          <template #[`item.created_at`]="{ item }">
            {{ formatDate(item.created_at) }}
          </template>
          <template #[`item.updated_at`]="{ item }">
            {{ formatDate(item.updated_at) }}
          </template>
          <template #[`item.edit`]="{ item }">
            <v-btn icon @click.stop="edit(item)">
              <v-icon small dark>mdi-cog-outline</v-icon>
            </v-btn>
          </template>
          <template #[`item.delete`]="{ item }">
            <v-btn icon @click.stop="openDeleteConfirmDialog(item)">
              <v-icon dark>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
        <v-pagination v-model="currentPage" :length="totalPages" :total-visible="7" color="secondary" @input="onPageChange" />
      </template>
    </v-base-card>

    <v-confirm-dialog
      ref="confirm"
      title="プリセット削除"
      :message="deleteMessage"
      buttonMessage="削除"
      @confirm="confirmDeletion"
    />
  </div>
</template>

<script>
import VBaseCard from '@/components/molecules/BaseCard'
import VConfirmDialog from '@/components/atoms/ConfirmDialog'

export default {
  components: {
    VBaseCard,
    VConfirmDialog,
  },
  data () {
    return {
      headers: [
        { text: 'コード', align: 'start', value: 'code' },
        { text: 'プリセット名', align: 'start', value: 'name' },
        { text: '教科', align: 'center', value: 'subject_name' },
        { text: '学年', align: 'center', value: 'grade_name' },
        { text: 'テスト時期', align: 'center', value: 'period_name' },
        { text: '年度', align: 'center', value: 'fiscal_year_western' },
        { text: 'ステータス', align: 'center', value: 'status' },
        { text: 'PDF', align: 'center', value: 'has_pdf', sortable: false },
        { text: '登録日', align: 'center', value: 'created_at' },
        { text: '更新日', align: 'center', value: 'updated_at' },
        { text: '編集', align: 'center', value: 'edit', sortable: false },
        { text: '削除', align: 'center', value: 'delete', sortable: false },
      ],
      presets: [],
      selectedSubjectId: null,
      selectedGradeId: null,
      selectedStatus: null,
      keyword: null,
      statusOptions: [
        { text: '下書き', value: 'draft' },
        { text: '公開中', value: 'published' },
      ],
      deletedPreset: { id: null, name: '' },
      loading: false,
      exporting: false,
      currentPage: 1,
      totalPages: 0,
      showImportArea: false,
      importFiles: [],
      importResults: [],
      uploading: false,
      dragging: false,
      sortBy: '',
      sortDesc: false,
    }
  },

  async mounted() {
    await this.getPresets()
  },

  watch: {
    selectedSubjectId() { this.search() },
    selectedGradeId() { this.search() },
    selectedStatus() { this.search() },
    sortBy() { this.onSortChange() },
    sortDesc() { this.onSortChange() },
  },

  computed: {
    deleteMessage() {
      return `${this.deletedPreset.name} (${this.deletedPreset.code}) を削除します。よろしいですか。`
    },
    subjects() {
      const allSubjects = this.$store.state.master.allSubjects
      return allSubjects ? allSubjects.filter(s => s.ancestry === null) : []
    },
    grades() {
      return this.$store.state.master.allGrades || []
    },
  },

  methods: {
    formatDate(dateStr) {
      if (!dateStr) return ''
      return dateStr.slice(0, 10)
    },
    async getPresets() {
      this.loading = true
      const params = new URLSearchParams()
      params.append('page', this.currentPage)
      if (this.selectedSubjectId) params.append('subject_id', this.selectedSubjectId)
      if (this.selectedGradeId) params.append('grade_id', this.selectedGradeId)
      if (this.selectedStatus) params.append('status', this.selectedStatus)
      if (this.keyword) params.append('keyword', this.keyword)
      if (this.sortBy) {
        params.append('sort_by', this.sortBy)
        params.append('sort_order', this.sortDesc ? 'desc' : 'asc')
      }
      const res = await this.$axios(`/api/v1/admin/presets?${params}`)
      this.presets = res.data.items
      this.totalPages = res.data.total_pages
      this.loading = false
    },
    search() {
      this.currentPage = 1
      this.getPresets()
    },
    onSortChange() {
      if (this._sortPending) return
      this._sortPending = true
      this.$nextTick(() => {
        this._sortPending = false
        this.currentPage = 1
        this.getPresets()
      })
    },
    onPageChange() {
      this.getPresets()
    },
    openDeleteConfirmDialog(preset) {
      this.deletedPreset = preset
      this.$refs.confirm.open()
    },
    async confirmDeletion() {
      const url = `api/v1/admin/presets/${this.deletedPreset.id}`
      await this.$axios.delete(url)
      this.$toasted.global.success({ message: `${this.deletedPreset.name}を削除しました` })
      this.getPresets()
    },
    async exportXlsx() {
      this.exporting = true
      try {
        const params = new URLSearchParams()
        if (this.selectedSubjectId) params.append('subject_id', this.selectedSubjectId)
        if (this.selectedGradeId) params.append('grade_id', this.selectedGradeId)
        const res = await this.$axios(`/api/v1/admin/presets/export?${params}`, { responseType: 'blob' })
        const contentDisposition = res.headers['content-disposition']
        let filename = 'プリセット一覧.xlsx'
        if (contentDisposition) {
          const match = contentDisposition.match(/filename\*?=(?:UTF-8'')?([^;\n]+)/i)
          if (match) filename = decodeURIComponent(match[1].replace(/["']/g, ''))
        }
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        this.$toasted.global.error({ message: 'エクスポートに失敗しました' })
      } finally {
        this.exporting = false
      }
    },
    edit(preset) {
      this.$router.push(`/admin/preset/${preset.id}`)
    },
    createNew() {
      this.$router.push('/admin/preset/new')
    },
    onDrop(e) {
      this.dragging = false
      const files = Array.from(e.dataTransfer.files).filter(f => /\.xlsx?$/i.test(f.name))
      if (files.length === 0) {
        this.$toasted.global.error({ message: 'XLSXファイルを選択してください' })
        return
      }
      this.importFiles = [...this.importFiles, ...files]
    },
    onFileSelect(e) {
      const files = Array.from(e.target.files).filter(f => /\.xlsx?$/i.test(f.name))
      this.importFiles = [...this.importFiles, ...files]
      e.target.value = ''
    },
    removeFile(index) {
      this.importFiles.splice(index, 1)
    },
    removeResult(index) {
      this.importResults.splice(index, 1)
    },
    async startImport() {
      if (!this.importFiles.length) return
      this.uploading = true
      try {
        for (const file of this.importFiles) {
          try {
            const formData = new FormData()
            formData.append('file', file)
            const res = await this.$axios.post('/api/v1/admin/presets/import', formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 600000,
            })
            const msg = res.data.messages
            this.importResults.push({
              filename: file.name,
              message: msg,
              success: msg === '成功',
            })
          } catch (e) {
            const errMsg = e.response && e.response.data ? e.response.data.messages : e.message
            this.importResults.push({
              filename: file.name,
              message: errMsg || '取り込みに失敗しました',
              success: false,
            })
          }
        }
        this.importFiles = []
        this.getPresets()
      } finally {
        this.uploading = false
      }
    },
  }
}
</script>

<style lang="stylus" scoped>
.select
  font-size 11px !important

.import-area
  padding 12px
  background #fafafa
  border 1px solid #e0e0e0
  border-radius 4px

.dropzone-area
  border 2px dashed #bdbdbd
  border-radius 6px
  padding 20px
  text-align center
  cursor pointer
  transition border-color 0.2s, background 0.2s

  &:hover
    border-color #4a81aa
    background #f0f6fb

  &.dropzone-active
    border-color #4a81aa
    background #e3f0fa

.dropzone-selected
  text-align left

.import-result-item
  padding 8px
  background #fff
  border 1px solid #e8e8e8
  border-radius 4px
</style>
